import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Toaster from "../utils/Toaster";
import { Box } from "@mui/material";
import { useTheme } from "../../theme/ThemeContext";
import AppButton from "../common/Button";
import { AppTypography } from "../common/Typography";
import Card from "../common/Card";
import { EmailInput } from "../common/Input";
import OTPInput from "../common/OTPInput";
import {
  useSendOTPMutation,
  useVerifyOTPMutation,
  useLoginWithAzureMutation,
} from "../../store/apiSlice";
import { useStoreInfo } from "../../context/StoreInfoContext";
import { AuthenticationMethod } from "../../models/AuthenticationMethod";
import loginPage from "../../assets/images/loginPage.jpeg";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";

const Login = () => {
  const theme = useTheme();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [azureLoading, setAzureLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = usePreserveStoreIdNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { customerId, storeId, storeInfo } = useStoreInfo();
  const [sendOTP, { isLoading: sendingOTP }] = useSendOTPMutation();
  const [verifyOTP, { isLoading: verifyingOTP }] = useVerifyOTPMutation();
  const [loginWithAzure] = useLoginWithAzureMutation();

  const isPrivateStore = storeInfo?.store?.is_public === false;
  const authenticationMethods = storeInfo?.authentication_methods || [];

  const enabledMethods = authenticationMethods.filter((method) => {
    if (method.hasOwnProperty("is_enabled")) {
      return method.is_enabled === 1;
    }
    return true;
  });

  const isEmailOTPEnabled = enabledMethods.some(
    (method) => method.label === AuthenticationMethod.EMAIL_OTP,
  );
  // const isGmailEnabled = enabledMethods.some(
  //   (method) => method.label === AuthenticationMethod.GMAIL
  // );
  const isAzureSSOEnabled = enabledMethods.some(
    (method) => method.label === AuthenticationMethod.AZURE_SSO_SAML,
  );
  // const isAWSSSOEnabled = enabledMethods.some(
  //   (method) => method.label === AuthenticationMethod.AWS_SSO_SAML
  // );

  const isSSOOnly = isPrivateStore && isAzureSSOEnabled && !isEmailOTPEnabled;
  const isOTPOnly = isPrivateStore && isEmailOTPEnabled && !isAzureSSOEnabled;
  const showBothMethods =
    isPrivateStore && isEmailOTPEnabled && isAzureSSOEnabled;

  const showDefaultMethods = !isPrivateStore && enabledMethods.length === 0;

  const shouldShowOTP = isPrivateStore
    ? isEmailOTPEnabled
    : isEmailOTPEnabled || showDefaultMethods;
  const shouldShowSSO = isPrivateStore ? isAzureSSOEnabled : isAzureSSOEnabled;
  const shouldShowOrDivider = shouldShowOTP && shouldShowSSO;

  useEffect(() => {
    if (isSSOOnly && step !== "email") {
      setStep("email");
      setOtp("");
      setResendTimer(0);
    }
  }, [isSSOOnly, step]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const error = searchParams.get("error");

    if (error) {
      const errorMessages = {
        login: "Login failed. Please try again.",
        "saml-login": "Error occurred during login. Please contact your admin.",
        saml: "Error occurred during login. Please contact your admin.", // Handle 'saml' error type from backend
      };

      Toaster.show(
        errorMessages[error] ||
          "An error occurred during login. Please contact your admin.",
        "error",
      );

      // Clean up URL by removing error parameter
      searchParams.delete("error");
      const newSearch = searchParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const handleEmailChange = (evt) => {
    setEmail(evt.target.value);
  };

  const handleOTPChange = (value) => {
    setOtp(value);
  };

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      Toaster.show("Please enter your email address", "warning");
      return;
    }
    if (!email.includes("@")) {
      Toaster.show("Please enter a valid email address", "warning");
      return;
    }

    setLoading(true);
    try {
      const result = await sendOTP({ customerId, storeId, email }).unwrap();
      setStep("otp");
      startResendTimer();
      Toaster.show(result.message || "OTP sent to your email!", "success");
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Failed to send OTP",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLogin = async () => {
    setAzureLoading(true);
    try {
      const result = await loginWithAzure().unwrap();
      if (result?.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }

      Toaster.show(result.message || "Login successful!", "success");
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1000);
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Failed to login with Azure",
        "error",
      );
    } finally {
      setAzureLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      Toaster.show("Please enter the 6-digit OTP", "warning");
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOTP({
        customerId,
        storeId,
        email,
        otp,
      }).unwrap();

      const userData = result.result?.user || {
        id: Date.now(),
        email: email,
        name: email.split("@")[0],
      };

      // Check for token in different possible locations
      const token =
        result.result?.token ||
        result.token ||
        result.result?.access_token ||
        result.access_token;

      login(userData, token);
      Toaster.show(result.message || "Login successful!", "success");

      // Redirect to the page they were trying to access, or home page
      const from = location.state?.from?.pathname || "/";
      setTimeout(() => navigate(from, { replace: true }), 1200);
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Invalid OTP",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    try {
      const result = await sendOTP({ customerId, storeId, email }).unwrap();
      startResendTimer();
      Toaster.show(result.message || "OTP resent successfully!", "success");
    } catch (error) {
      Toaster.show(
        error.data?.message || error.message || "Failed to resend OTP",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setOtp("");
    setResendTimer(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        position: "fixed",
        top: 0,
        left: 0,
        ...theme?.background,
      }}
    >
      {/* Left Half - Image */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={theme?.login_page_image_url || loginPage}
          alt="Login"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
      </Box>

      {/* Right Half - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: {
            xs: theme?.background?.background || "#fff",
            md: "rgba(255,255,255,0.85)",
          },
          backdropFilter: { md: "blur(6px)" },
          boxShadow: { md: "-8px 0 32px 0 rgba(0,0,0,0.08)" },
          transition: "background 0.3s",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            px: { xs: 1, sm: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
            }}
          >
            <AppTypography
              variant="h3"
              style={theme?.typography?.heading}
              fontWeight={700}
              fontSize={{ xs: "1.75rem", sm: "2rem", md: "2.2rem" }}
              textAlign="center"
              mb={1}
            >
              {theme?.login_page_heading_text ||
                (isSSOOnly
                  ? "Login with SSO"
                  : isOTPOnly
                    ? step === "email"
                      ? "Login with OTP"
                      : "Enter OTP"
                    : step === "email"
                      ? "Login"
                      : "Enter OTP")}
            </AppTypography>
            <AppTypography
              variant="body1"
              style={theme?.typography?.body}
              fontSize={{ xs: "0.9rem", sm: "1rem", md: "1.1rem" }}
              textAlign="center"
              sx={{ opacity: 0.9 }}
            >
              {(() => {
                if (theme?.login_page_subheading_text !== undefined) {
                  return theme.login_page_subheading_text;
                }

                if (isSSOOnly) {
                  return "Please login using your Azure SSO credentials.";
                }

                if (isOTPOnly) {
                  return step === "email"
                    ? "Enter your registered email address, and we'll send you a OTP to log in securely."
                    : `We've sent a 6-digit code to ${email}`;
                }

                return step === "email"
                  ? "Enter your registered email address, and we'll send you a OTP to log in securely."
                  : `We've sent a 6-digit code to ${email}`;
              })()}
            </AppTypography>
          </Box>

          {/* Login Card */}
          <Card
            variant="elevated"
            sx={{
              width: "100%",
              maxWidth: 400,
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              boxShadow: "0 8px 32px 0 rgba(0,0,0,0.10)",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            {isSSOOnly ? (
              // SSO Only Mode
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  width: "100%",
                }}
              >
                <AppButton
                  variant="contained"
                  size="large"
                  disabled={azureLoading}
                  onClick={handleAzureLogin}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    width: "100%",
                    backgroundColor: theme?.app_button_color,
                    color: theme?.app_button_text_color,
                    "&:hover": {
                      backgroundColor: theme?.app_hover_button_color,
                    },
                  }}
                >
                  {azureLoading ? "Loading..." : "Login With Azure (SSO)"}
                </AppButton>
              </Box>
            ) : isOTPOnly ? (
              // OTP Only Mode
              step === "email" ? (
                <Box
                  component="form"
                  onSubmit={handleSendOTP}
                  autoComplete="on"
                  noValidate
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    width: "100%",
                  }}
                >
                  <EmailInput
                    name="email"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="username"
                    required
                    size="large"
                    placeholder="Enter your email address"
                  />

                  <AppButton
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || sendingOTP}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 2,
                      width: "100%",
                      backgroundColor: theme?.app_button_color,
                      color: theme?.app_button_text_color,
                      "&:hover": {
                        backgroundColor: theme?.app_hover_button_color,
                      },
                    }}
                  >
                    {loading || sendingOTP ? "Sending..." : "Send OTP"}
                  </AppButton>
                </Box>
              ) : (
                // OTP Step
                <Box
                  component="form"
                  onSubmit={handleVerifyOTP}
                  autoComplete="off"
                  noValidate
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    width: "100%",
                  }}
                >
                  {/* OTP Input */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <AppTypography
                      variant="body2"
                      style={theme?.typography?.body}
                      fontSize="0.875rem"
                      textAlign="center"
                      sx={{ opacity: 0.7 }}
                    >
                      Enter the 6-digit code
                    </AppTypography>
                    <OTPInput
                      value={otp}
                      onChange={handleOTPChange}
                      disabled={loading}
                    />
                  </Box>

                  {/* Verify OTP Button */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <AppButton
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading || verifyingOTP || otp.length !== 6}
                      sx={{
                        mt: 2,
                        py: { xs: 0.75, sm: 1 },
                        px: { xs: 2, sm: 3 },
                        fontSize: { xs: "0.95rem", sm: "1.1rem" },
                        fontWeight: 600,
                        borderRadius: 2,
                        width: { xs: "fit-content", sm: "fit-content" },
                        minWidth: { xs: "200px", sm: "220px" },
                        maxWidth: { xs: "280px", sm: "320px", md: "340px" },
                        backgroundColor: theme?.app_button_color,
                        color: theme?.app_button_text_color,
                        "&:hover": {
                          backgroundColor: theme?.app_hover_button_color,
                        },
                      }}
                    >
                      {loading || verifyingOTP ? "Verifying..." : "Verify OTP"}
                    </AppButton>
                  </Box>

                  {/* Resend OTP */}
                  <Box sx={{ textAlign: "center" }}>
                    <AppTypography
                      variant="body2"
                      style={theme?.typography?.body}
                      fontSize="0.875rem"
                      textAlign="center"
                      sx={{ opacity: 0.7 }}
                    >
                      Didn't receive the code?{" "}
                      <AppTypography
                        component="button"
                        type="button"
                        onClick={handleResendOTP}
                        disabled={resendTimer > 0}
                        style={theme?.typography?.link}
                        fontSize="0.875rem"
                        fontWeight={600}
                        sx={{
                          textDecoration: "none",
                          cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                          border: "none",
                          background: "none",
                          "&:hover": {
                            textDecoration:
                              resendTimer > 0 ? "none" : "underline",
                          },
                          opacity: resendTimer > 0 ? 0.5 : 1,
                        }}
                      >
                        {resendTimer > 0
                          ? `Resend in ${resendTimer}s`
                          : "Resend OTP"}
                      </AppTypography>
                    </AppTypography>
                  </Box>

                  {/* Back to Email */}
                  <Box sx={{ textAlign: "center" }}>
                    <AppTypography
                      component="button"
                      type="button"
                      onClick={handleBackToEmail}
                      style={theme?.typography?.link}
                      fontSize="0.875rem"
                      fontWeight={500}
                      sx={{
                        textDecoration: "none",
                        cursor: "pointer",
                        border: "none",
                        background: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      ← Back to email
                    </AppTypography>
                  </Box>
                </Box>
              )
            ) : step === "email" ? (
              // Email Step
              <Box
                component="form"
                onSubmit={handleSendOTP}
                autoComplete="on"
                noValidate
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  width: "100%",
                }}
              >
                {/* Only show email input if OTP is available (not SSO only) */}
                {shouldShowOTP && (
                  <EmailInput
                    name="email"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="username"
                    required
                    size="large"
                    placeholder="Enter your email address"
                  />
                )}

                {/* Show OTP button first if Email OTP is enabled or default mode */}
                {shouldShowOTP && (
                  <AppButton
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || sendingOTP}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 2,
                      width: "100%",
                      backgroundColor: theme?.app_button_color,
                      color: theme?.app_button_text_color,
                      "&:hover": {
                        backgroundColor: theme?.app_hover_button_color,
                      },
                    }}
                  >
                    {loading || sendingOTP ? "Sending..." : "Login with OTP"}
                  </AppButton>
                )}

                {/* Show "Or" divider if both methods are available */}
                {shouldShowOrDivider && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      my: 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                      }}
                    />
                    <AppTypography
                      variant="body2"
                      style={theme?.typography?.body}
                      sx={{
                        px: 2,
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Or
                    </AppTypography>
                    <Box
                      sx={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                      }}
                    />
                  </Box>
                )}

                {/* Show SSO button below if Azure SSO is enabled or default mode */}
                {shouldShowSSO && (
                  <AppButton
                    type="button"
                    variant="outlined"
                    size="large"
                    disabled={azureLoading || loading || sendingOTP}
                    onClick={handleAzureLogin}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 2,
                      width: "100%",
                      backgroundColor: theme?.app_button_color,
                      color: theme?.app_button_text_color,
                      "&:hover": {
                        backgroundColor: theme?.app_hover_button_color,
                      },
                    }}
                  >
                    {azureLoading ? "Loading..." : "Login With Azure (SSO)"}
                  </AppButton>
                )}
              </Box>
            ) : (
              // OTP Step
              <Box
                component="form"
                onSubmit={handleVerifyOTP}
                autoComplete="off"
                noValidate
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  width: "100%",
                }}
              >
                {/* OTP Input */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <AppTypography
                    variant="body2"
                    style={theme?.typography?.body}
                    fontSize="0.875rem"
                    textAlign="center"
                    sx={{ opacity: 0.7 }}
                  >
                    Enter the 6-digit code
                  </AppTypography>
                  <OTPInput
                    value={otp}
                    onChange={handleOTPChange}
                    disabled={loading}
                  />
                </Box>

                {/* Verify OTP Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <AppButton
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || verifyingOTP || otp.length !== 6}
                    sx={{
                      mt: 2,
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 2, sm: 3 },
                      fontSize: { xs: "0.95rem", sm: "1.1rem" },
                      fontWeight: 600,
                      borderRadius: 2,
                      width: { xs: "fit-content", sm: "fit-content" },
                      minWidth: { xs: "200px", sm: "220px" },
                      maxWidth: { xs: "280px", sm: "320px", md: "340px" },
                      backgroundColor: theme?.app_button_color,
                      color: theme?.app_button_text_color,
                      "&:hover": {
                        backgroundColor: theme?.app_hover_button_color,
                      },
                    }}
                  >
                    {loading || verifyingOTP ? "Verifying..." : "Verify OTP"}
                  </AppButton>
                </Box>

                {/* Resend OTP */}
                <Box sx={{ textAlign: "center" }}>
                  <AppTypography
                    variant="body2"
                    style={theme?.typography?.body}
                    fontSize="0.875rem"
                    textAlign="center"
                    sx={{ opacity: 0.7 }}
                  >
                    Didn't receive the code?{" "}
                    <AppTypography
                      component="button"
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0}
                      style={theme?.typography?.link}
                      fontSize="0.875rem"
                      fontWeight={600}
                      sx={{
                        textDecoration: "none",
                        cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                        border: "none",
                        background: "none",
                        "&:hover": {
                          textDecoration:
                            resendTimer > 0 ? "none" : "underline",
                        },
                        opacity: resendTimer > 0 ? 0.5 : 1,
                      }}
                    >
                      {resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Resend OTP"}
                    </AppTypography>
                  </AppTypography>
                </Box>

                {/* Back to Email */}
                <Box sx={{ textAlign: "center" }}>
                  <AppTypography
                    component="button"
                    type="button"
                    onClick={handleBackToEmail}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={500}
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      border: "none",
                      background: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    ← Back to email
                  </AppTypography>
                </Box>
              </Box>
            )}
          </Card>

          {/* Registration Option */}
          {/* <Box sx={{ textAlign: "center", mt: 2 }}>
            <AppTypography
              variant="body2"
              style={theme?.typography?.body}
              fontSize="0.875rem"
              textAlign="center"
              sx={{ opacity: 0.7 }}
            >
              Don't have an account?{" "}
              <AppTypography
                component="button"
                type="button"
                onClick={() => navigate("/register")}
                style={theme?.typography?.link}
                fontSize="0.875rem"
                fontWeight={600}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Register here
              </AppTypography>
            </AppTypography>
          </Box> */}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
